'use client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { GroundedWorkMediaContextBanner } from '@/components/org-chat/artifact-thread-panel/grounded-work-media-context-banner';
import { FullscreenViewerMedia } from '@/components/ui/fullscreen-viewer-media';
import { getImageAnnotationCopy } from '@/i18n/image-annotation-copy';
import { getStaticUiCopy } from '@/i18n/static-ui-copy';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, PenTool, X } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';

interface FullscreenImageViewerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  src: string;
  path?: string;
  alt: string;
  title?: string;
  type?: 'image' | 'video' | '3d';
  assets?: Array<{
    src: string;
    path?: string;
    alt?: string;
    title?: string;
    type?: 'image' | 'video' | '3d';
  }>;
  initialIndex?: number;
  orgSlug?: string;
  onOpenWorkspacePath?: (path: string) => void;
  /** Open the active workspace image in the artifact panel's annotation. */
  onAnnotateWorkspacePath?: (path: string) => void;
}

export function FullscreenImageViewer({
  isOpen,
  onOpenChange,
  src,
  path,
  alt,
  title,
  type = 'image',
  assets,
  initialIndex = 0,
  orgSlug,
  onOpenWorkspacePath,
  onAnnotateWorkspacePath,
}: FullscreenImageViewerProps) {
  const locale = useLocale();
  const ui = getStaticUiCopy(locale);
  const annotationCopy = getImageAnnotationCopy(locale);
  const hasCarousel: boolean = Array.isArray(assets) && assets.length > 0;
  const [currentIndex, setCurrentIndex] = useState(() => initialIndex);
  const [showControls, setShowControls] = useState(false);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaContainerRef = useRef<HTMLDivElement | null>(null);
  const thumbnailStripRef = useRef<HTMLDivElement | null>(null);
  const previousInputsRef = useRef<{
    src: string;
    initialIndex: number;
    assetsLength: number;
  } | null>(null);

  // Keep index in range when inputs change - only update when inputs actually change
  useEffect(() => {
    if (!hasCarousel || !assets?.length) return;

    const max = assets.length;
    const bySrc = src ? assets.findIndex((a) => (a?.src ?? '') === src) : -1;
    const candidateIndex = bySrc >= 0 ? bySrc : initialIndex;
    const nextIndex = Math.min(
      Math.max(candidateIndex, 0),
      Math.max(0, max - 1)
    );

    const currentInputs = { src: src ?? '', initialIndex, assetsLength: max };
    const prevInputs = previousInputsRef.current;

    // Only update if inputs actually changed
    if (
      !prevInputs ||
      prevInputs.src !== currentInputs.src ||
      prevInputs.initialIndex !== currentInputs.initialIndex ||
      prevInputs.assetsLength !== currentInputs.assetsLength
    ) {
      // Guard: only update if the index actually needs to change
      requestAnimationFrame(() => {
        setCurrentIndex((prev) => (prev !== nextIndex ? nextIndex : prev));
        previousInputsRef.current = currentInputs;
      });
    }
  }, [hasCarousel, assets, src, initialIndex]);

  const modulo = useCallback((n: number, m: number) => ((n % m) + m) % m, []);
  const handlePrev = useCallback(() => {
    if (!hasCarousel || !assets?.length) return;
    setCurrentIndex((idx) => modulo(idx - 1, assets.length));
  }, [hasCarousel, assets, modulo]);
  const handleNext = useCallback(() => {
    if (!hasCarousel || !assets?.length) return;
    setCurrentIndex((idx) => modulo(idx + 1, assets.length));
  }, [hasCarousel, assets, modulo]);

  const handleViewerKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!hasCarousel) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    },
    [handleNext, handlePrev, hasCarousel]
  );

  // Auto-hide controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Scroll thumbnail strip to show current thumbnail
  useEffect(() => {
    if (!hasCarousel || !thumbnailStripRef.current) return;
    const thumbnailWrapper = thumbnailStripRef.current.children[
      currentIndex
    ] as HTMLElement;
    if (thumbnailWrapper) {
      thumbnailWrapper.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentIndex, hasCarousel]);

  const active =
    hasCarousel && assets?.length
      ? assets[Math.min(Math.max(currentIndex, 0), assets.length - 1)]
      : { src, alt, title, type, path };
  const activeSrc = active?.src ?? '';
  const activeAlt = active?.alt ?? alt;
  const activeTitle = active?.title ?? title;
  const activeType = (active?.type ?? type) as 'image' | 'video' | '3d';
  // Annotation lives in the artifact panel, which needs the image's path.
  const annotatePath =
    onAnnotateWorkspacePath && activeType === 'image' && active?.path
      ? active.path
      : null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-background !fixed !inset-0 !top-0 !left-0 !z-[10001] !m-0 !flex !h-dvh !max-h-[100dvh] !w-screen !max-w-[100vw] !translate-x-0 !translate-y-0 !items-center !justify-center !rounded-none !border-none !p-0 !shadow-none"
        showCloseButton={false}
        onKeyDown={handleViewerKeyDown}
        onMouseMove={handleMouseMove}
      >
        <DialogTitle className="sr-only">
          {activeTitle || activeAlt}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {ui.fullscreenViewerDescription}
        </DialogDescription>
        <div
          ref={mediaContainerRef}
          className="relative flex h-full w-full items-center justify-center"
          onClick={(e) => {
            // Close if clicking on empty space (container background, not children)
            if (e.target === e.currentTarget) {
              onOpenChange(false);
            }
          }}
        >
          {isOpen && active?.path && orgSlug && onOpenWorkspacePath ? (
            <GroundedWorkMediaContextBanner
              key={active.path}
              className={annotatePath ? 'top-16 md:top-3' : undefined}
              orgSlug={orgSlug}
              sourcePath={active.path}
              onOpenWorkspacePath={(path) => {
                onOpenChange(false);
                onOpenWorkspacePath(path);
              }}
            />
          ) : null}
          <div className="fixed top-4 right-4 z-[10002] flex items-center gap-2">
            {annotatePath && onAnnotateWorkspacePath ? (
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenChange(false);
                  onAnnotateWorkspacePath(annotatePath);
                }}
                className="bg-background/80 gap-2 rounded-lg px-3 py-2 backdrop-blur-sm"
              >
                <PenTool className="h-4 w-4" aria-hidden />
                <span className="text-sm font-medium">
                  {annotationCopy.annotate}
                </span>
              </Button>
            ) : null}
            {/* Desktop: Close Button - top right with ESC text */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="bg-background/80 hidden gap-2 rounded-lg px-3 py-2 backdrop-blur-sm md:flex"
            >
              <X className="h-4 w-4" />
              <span className="text-sm font-medium">{ui.esc}</span>
            </Button>
          </div>

          {/* Desktop: Prev/Next Controls - fade in/out with mouse movement */}
          {hasCarousel && assets && assets.length > 1 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className={cn(
                  'bg-background/80 fixed top-1/2 left-4 z-[10002] hidden -translate-y-1/2 rounded-lg backdrop-blur-sm transition-opacity duration-300 md:flex',
                  showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
                aria-label={ui.previous}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                className={cn(
                  'bg-background/80 fixed top-1/2 right-4 z-[10002] hidden -translate-y-1/2 rounded-lg backdrop-blur-sm transition-opacity duration-300 md:flex',
                  showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
                aria-label={ui.next}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}

          {/* Thumbnail Strip - shown for carousel on both desktop and mobile */}
          {hasCarousel && assets && assets.length > 1 && (
            <div
              className="from-background via-background/60 pointer-events-none fixed right-0 bottom-0 left-0 z-40 flex items-center justify-center bg-gradient-to-t to-transparent px-4 pt-8 pb-20 md:pb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                ref={thumbnailStripRef}
                className="pointer-events-auto flex max-w-full [scrollbar-width:none] gap-2 overflow-x-auto py-2 pb-2 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {assets.map((asset, index) => (
                  <div
                    key={`${asset.src}-${index}`}
                    className={cn(
                      'flex flex-shrink-0 items-center justify-center transition-all',
                      index === currentIndex ? 'p-1' : 'p-0.5'
                    )}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentIndex(index);
                      }}
                      aria-label={asset.alt || `View item ${index + 1}`}
                      className={cn(
                        'relative h-16 w-16 overflow-hidden rounded-lg border-2 p-0 transition-all md:h-14 md:w-14',
                        index === currentIndex
                          ? 'border-foreground scale-105'
                          : 'border-border/50 opacity-60 hover:opacity-80'
                      )}
                    >
                      {asset.type === 'image' ? (
                        <div className="relative h-full w-full">
                          <Image
                            src={asset.src}
                            alt={asset.alt || `Thumbnail ${index + 1}`}
                            width={80}
                            height={80}
                            className="h-full w-full object-cover"
                            unoptimized
                          />
                        </div>
                      ) : asset.type === 'video' ? (
                        <div className="flex h-full w-full items-center justify-center bg-black/50">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white">
                            <div className="ml-0.5 h-0 w-0 border-t-[4px] border-b-[4px] border-l-[6px] border-t-transparent border-b-transparent border-l-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-black/50">
                          <div className="h-6 w-6 rounded border-2 border-white" />
                        </div>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mobile: Bottom controls with Close button and navigation */}
          {hasCarousel && assets && assets.length > 1 ? (
            <div
              className="border-border/35 bg-background/80 fixed right-0 bottom-0 left-0 z-[10002] flex items-center justify-between gap-2 border-t p-4 backdrop-blur-sm md:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                className="flex-1 rounded-lg py-3"
                aria-label={ui.previous}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                className="flex-1 rounded-lg py-3 font-medium"
              >
                {ui.close}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="flex-1 rounded-lg py-3"
                aria-label={ui.next}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <div
              className="border-border/35 bg-background/80 fixed right-0 bottom-0 left-0 z-[10002] flex items-center justify-center border-t p-4 backdrop-blur-sm md:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                className="w-full max-w-xs rounded-lg py-3 font-medium"
              >
                {ui.close}
              </Button>
            </div>
          )}

          {/* Media content */}
          {activeSrc && (
            <FullscreenViewerMedia
              key={`${activeSrc}-${currentIndex}`}
              src={activeSrc}
              alt={activeAlt}
              type={activeType}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
